import { computed, markRaw, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { TaskType } from '@types'
import { AlertInstance } from '@/plugins/alerts/utils/types'

import { ROUTE_NAME } from '@/router/utils/const'
import { DASHBOARD_NAME } from '@/store/navigation/utils/const'
import { NOTIFICATION_DELAY } from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { useAlerts } from '@/plugins/alerts'
import { NotificationInstance, useNotifications } from '@/plugins/notification'

import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsStore } from '@/store/assets/index'
import { useLinkedDataStore } from '@/store/linkedData'
import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'
import { useLinkedDataTransactionsStore } from '@/store/linkedData/transactions'
import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useTasksStore } from '@/store/tasks'
import { useTransactionsStore } from '@/store/transactions'
import { useTagsStore } from '@/store/tags'
import { UIPreloader } from '@ui/core'

export const useAlertsService = () => {
  // INIT
  const { progress, remove: removeAlert, update: updateAlert } = useAlerts()
  const { success, remove: removeNotification } = useNotifications()

  const router = useRouter()

  const analyticsStore = useAnalyticsStore()
  const assetsStore = useAssetsStore()
  const linkedDataStore = useLinkedDataStore()
  const linkedDataConnectorsStore = useLinkedDataConnectorsStore()
  const linkedDataTransactionsStore = useLinkedDataTransactionsStore()
  const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()
  const repositoriesStore = useRepositoriesStore()
  const tagsStore = useTagsStore()
  const tasksStore = useTasksStore()
  const transactionsStore = useTransactionsStore()

  const { syncing, mapped } = useLinkedDataConnector()

  const importAlertId = ref<string>()
  const syncAlert = ref<AlertInstance>()
  const syncNotification = ref<NotificationInstance>()

  // GETTERS
  const isRepositoryImporting = computed(
    () =>
      !!tasksStore.getProcessing.find(task =>
        task.type.startsWith(TaskType.REPOSITORY_IMPORT),
      ),
  )

  const syncNotificationLink = computed(() => {
    return {
      text: 'Go back to connectors',
      to: {
        name: ROUTE_NAME.LINKED_DATA,
      },
    }
  })

  // WATCHERS
  watch(() => ({
    syncing: syncing.value,
    mapped: mapped.value,
  }),
  async ({ syncing, mapped }) => {
    // checking if syncing is totaly done and accounts are not in mapped state
    if (syncing && !mapped) {
      syncAlert.value && (await removeAlert(syncAlert.value.id))
      const message = 'Your accounts are now being refreshed. This may take several minutes...'
      const alert = await progress({
        message,
        icon: markRaw(UIPreloader),
      })
      syncAlert.value = alert
      const currentRoute = localStorage.getItem(`nav-${DASHBOARD_NAME}`)
      router.push(currentRoute ? JSON.parse(currentRoute) : { name: ROUTE_NAME.DASHBOARDS })
      syncNotification.value = await success({
        message,
        link: syncNotificationLink.value,
      }, NOTIFICATION_DELAY)
    } else if (syncAlert.value) {
      try {
        // accounts are mapped, but still syncing
        if (mapped) {
          const message = 'Your accounts are mapped. Waiting for linked data to be processed...'
          updateAlert(syncAlert.value.id, { message })
          syncNotification.value && removeNotification(syncNotification.value)
          syncNotification.value = undefined
          await success({
            message,
            link: syncNotificationLink.value,
          }, NOTIFICATION_DELAY)
          Promise.all([
            assetsStore.fetch(),
            transactionsStore.fetch(),
            tagsStore.fetch(),
          ])
        } else {
          // syncing is done
          await removeAlert(syncAlert.value.id)
          syncAlert.value = undefined
          Promise.all([
            assetsStore.fetch(),
            transactionsStore.fetch(),
            tagsStore.fetch(),
            linkedDataStore.fetch(),
            linkedDataTransactionsStore.fetch(),
            linkedDataTransactionsTagsStore.fetch(),
            linkedDataConnectorsStore.fetch(),
          ])
        }
        analyticsStore.markAsDeprecated()
      } catch (e) {
        handleCatchedError(e as string)
      }
    }
  },
  { immediate: true },
  )

  watch(isRepositoryImporting, async values => {
    if (values) {
      importAlertId.value && (await removeAlert(importAlertId.value))
      const alert = await progress({
        message:
          'Your repository is now being imported. This may take several minutes.',
        icon: markRaw(UIPreloader),
      })
      importAlertId.value = alert.id
    } else if (importAlertId.value) {
      await removeAlert(importAlertId.value)
      importAlertId.value = undefined
      repositoriesStore.fetch()
    }
  })
}
